/**
 * WP14 (docs/decisions/2026-09-24-wp14-kierunek-transportu-i-odbior-wlasny.md):
 * every transport has a direction, and it alone decides who pays its freight:
 *   INBOUND  — od dostawcy: freight goes into the ZD batches only;
 *   OUTBOUND — do klienta: freight is split across the customer orders only.
 * The money rule lives in the backend (transportService); this module is the
 * UI mirror of backend/src/utils/transportDirection.js — values, the default
 * for a new truck and the Polish label keys.
 */
export type TransportDirection = 'INBOUND' | 'OUTBOUND';
export type OrderDeliveryMode = 'OUR_DELIVERY' | 'CUSTOMER_PICKUP';

export const TRANSPORT_DIRECTIONS: TransportDirection[] = ['INBOUND', 'OUTBOUND'];
export const ORDER_DELIVERY_MODES: OrderDeliveryMode[] = ['OUR_DELIVERY', 'CUSTOMER_PICKUP'];

export function normalizeTransportDirection(value: unknown): TransportDirection | null {
  return value === 'INBOUND' || value === 'OUTBOUND' ? value : null;
}

export function normalizeOrderDeliveryMode(value: unknown): OrderDeliveryMode {
  return value === 'CUSTOMER_PICKUP' ? 'CUSTOMER_PICKUP' : 'OUR_DELIVERY';
}

/**
 * Suggested direction of a new truck until the operator picks one: a truck
 * carrying a supplier order comes from the supplier (also with customer orders
 * on board — the direct delivery), otherwise it goes to the customer. Same rule
 * as the backend default for a writer that omits the direction.
 */
export function deriveTransportDirection(
  supplierOrderCount: number,
): TransportDirection {
  return supplierOrderCount > 0 ? 'INBOUND' : 'OUTBOUND';
}

export function isCustomerPickup(order?: { deliveryMode?: OrderDeliveryMode | null } | null): boolean {
  return order?.deliveryMode === 'CUSTOMER_PICKUP';
}

/** i18n keys in the `transport` namespace. */
export const transportDirectionLabelKey = (direction: TransportDirection) => `transport:direction.options.${direction}`;
export const transportDirectionShortKey = (direction: TransportDirection) => `transport:direction.short.${direction}`;

/** i18n keys in the `orders` namespace. */
export const orderDeliveryModeLabelKey = (mode: OrderDeliveryMode) => `orders:deliveryMode.options.${mode}`;
